import { totalCoverageConfig } from '../configs/totalCoverageConfig';
import { ROUTES } from '../lib/constants';
import {
  PRODUCTS,
  SESSION_KEYS,
  MAX_AREA_ADDITIONAL_BUILDINGS,
  ADD_ON_PACKAGE_DISABLED_PARTNERS,
  HOME_COMMON_PARTNERS_CHANNELS
} from '../lib/constants/appConstants';
import {
  errorHeader,
  shouldAllowErrorRetry
} from '../components/Common/ErrorPage/TemporaryErrorPageHelper';
import {
  IPricingQuestion,
  IValueOptions
} from '../interfaces/EnquiryInterface';
import {
  IAdditionalPackageData,
  IPackageData
} from '../interfaces/PackageInterface';
import {
  Coverage,
  ErrorStatus,
  HeaderTab,
  Offer,
  OfferResponseAPI,
  Package,
  PricingStepConfig,
  TotalPriceInfo,
  NonEmptyArray
} from '../interfaces/Types';
import {
  AdditionalInputs,
  PricingQuestion,
  TotalCoverageInfo
} from '../interfaces/Types';
import { TextHelper } from '../utils/TextHelper';
import { APIHelper } from './ApiHelper';
import { AppConstants } from './AppConstants';
import { getRoofConstructionRange } from './Calculation/CalculationService';
import { ConfigHelper } from './ConfigHelper';
import { logMessage } from './loggers';

export class InsuranceHelper {
  static defaultSelectedPackage = '';

  static setDefaultSelectedPackage = (packageName: string) => {
    InsuranceHelper.defaultSelectedPackage = packageName || '';
  };

  static getDefaultSelectedPackage = () =>
    InsuranceHelper.defaultSelectedPackage;

  static getChannelInfo = () => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    return customerInfo?.channel &&
      customerInfo?.channel?.toLowerCase() !== 'gjensidige'
      ? customerInfo.channel
      : 'Gjensidige';
  };

  static getProductChannelInfo = () => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    if (HOME_COMMON_PARTNERS_CHANNELS.includes(customerInfo?.channel)) {
      return 'common-partners'
    }
    return InsuranceHelper.getChannelInfo();
  };

  static isEmptyObject = (obj: any) => Object.keys(obj).length === 0;

  static isPropertyDataPresent = (product: string) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    return product === PRODUCTS.VILLA && Boolean(customerInfo?.propertyData);
  };

  static getPropertyInfo = (product: string) => {
    let propertyInfo = {
      isPresent: false,
      propertyDataMessage: ''
    };
    if (product === PRODUCTS.VILLA) {
      const customerInfo = JSON.parse(
        sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
      );
      propertyInfo = {
        isPresent: Boolean(customerInfo?.propertyData),
        propertyDataMessage: TextHelper.getPropertyDataMessage(
          Number(customerInfo?.noOfProperties) || 0
        )
      };
    }
    return propertyInfo;
  };
  static retrievePolicyInfoFromURL = () => {
    const urlParams: string = document.location.href.split('?')[1] || '';
    const policyInfo = {
      policyNumber: '',
      customerNumber: '',
      couponCode: '',
      couponDiscountPercentage: '',
      hasSessionData: false
    };
    if (urlParams) {
      const searchParams = new URLSearchParams(urlParams);
      const sessionKey: string = searchParams.get('sessionKey') || '';
      if (sessionKey) {
        const dataFromSession = JSON.parse(
          sessionStorage.getItem(sessionKey) || '{}'
        );
        policyInfo.policyNumber =
          (dataFromSession && dataFromSession.policyNr) || '';
        policyInfo.customerNumber =
          (dataFromSession && dataFromSession.customerNr) || '';
        policyInfo.couponCode =
          (dataFromSession && dataFromSession.coupon) || '';
        policyInfo.couponDiscountPercentage =
          (dataFromSession && dataFromSession.couponDiscountPercentage) || '';
        policyInfo.hasSessionData = true;
      } else {
        const policyNumber: string = searchParams.get('policyNumber') || '';
        const channel: string = searchParams.get('channel') || '';
        if (policyNumber) {
          policyInfo.policyNumber = policyNumber;
          policyInfo.hasSessionData = false;
          logMessage(`Customer opened offer with policy number: ${policyNumber} and channel: ${channel}`, { severity: 'INFO' });
        }
      }
    }
    return policyInfo;
  };

  // static getPolicyNumberFromUrl = () => {
  //   const urlParams: string = document.location.href.split('?')[1] || '';
  //   const searchParams = new URLSearchParams(urlParams);
  //   return searchParams.get('policyNumber') || '';
  // };

  static getPolicyAndChannelFromUrl = () => {
    const urlParams: string = document.location.href.split('?')[1] || '';
    const searchParams = new URLSearchParams(urlParams);
    return {
      policyNumber: searchParams.get('policyNumber') || '',
      channel: searchParams.get('channel') || ''
    }
  };

  static getDefaultPackageSelected = (
    insurancePackageList: IPackageData[],
    defaultPackage: string
  ) => {
    let selectedPackageType = 'helforsakring';
    insurancePackageList?.forEach((packageItem: IPackageData) => {
      if (
        packageItem.packageType.trim().toLocaleUpperCase() ===
        defaultPackage.trim().toLocaleUpperCase()
      ) {
        selectedPackageType = packageItem.packageType.trim();
      }
    });
    return selectedPackageType;
  };

  static getSmallScreenTabs = (packages: Package[]) => {
    const tabs: HeaderTab[] = [];
    packages.forEach((packageItem: Package) => {
      if (!packageItem.isPlusPackage) {
        tabs.push({
          packageName: packageItem.packageName,
          packageCode: packageItem.packageCode,
          isSelected: packageItem.isSelected,
          isEnabled: packageItem.isEnabled,
          defaultPackage: packageItem.defaultPackage,
          defaultPackageMessage: packageItem.defaultPackageMessage
        });
      }
    });
    //Sorting to show the recommended package always first
    tabs.sort((a) => (a.defaultPackage && a.defaultPackageMessage ? -1 : 1));
    return tabs;
  };

  static getDefaultValueForAdditionalInputs = (
    product: string,
    question: IPricingQuestion
  ) => {
    let defaultValue = '';
    if (product === PRODUCTS.VILLA && question.APIKey === 'extraFront') {
      defaultValue = '3';
    }
    return defaultValue;
  };

  static getDefaultDeductibleSelections = (
    productName: string,
    deductibleList: any
  ) => {
    if (productName === PRODUCTS.CAR) {
      return {
        delkaskoSelfrisk: APIHelper.getDefaultSelection(
          deductibleList,
          'delkaskoSelfRisk'
        ),
        vagnskadaSelfrisk: APIHelper.getDefaultSelection(
          deductibleList,
          'vagnaskadaSelfRisk'
        )
      };
    }
    return {};
  };

  static buildDeductibleSelection = (
    selectedOption: string,
    deductibleType: string,
    productName: string
  ) => {
    let updatedOption = {};
    if (productName === PRODUCTS.CAR) {
      if (deductibleType.toLowerCase() === 'vagnskada') {
        updatedOption = {
          vagnskadaSelfrisk: selectedOption
        };
      } else {
        updatedOption = {
          delkaskoSelfrisk: selectedOption
        };
      }
    }
    return updatedOption;
  };

  static getSelectedPackage = (insurancePackageList: any) => {
    return insurancePackageList && insurancePackageList.length > 0
      ? insurancePackageList.filter((packageItem: any) => packageItem.selected)
      : [];
  };

  static getSelectedPackageFromConfig = (insurancePackageList: any) => {
    return insurancePackageList.length > 0
      ? insurancePackageList.filter(
        (packageItem: any) => packageItem.isSelected
      )
      : [];
  };

  static getTotalPackageLabel = (
    selectedPackage: IPackageData,
    additionalPackageList: IAdditionalPackageData[],
    isAdditionalPackageSelected: boolean
  ) => {
    if (!InsuranceHelper.isEmptyObject(selectedPackage)) {
      return isAdditionalPackageSelected
        ? `${selectedPackage?.packageName} + ${additionalPackageList[0].packageName}`
        : selectedPackage.packageName;
    }
    return '';
  };

  static getCoverageValueFromSelectedPackage = (
    selectedPackage: Package[],
    coverageName: string
  ) => {
    const selectedMainPackage = selectedPackage.find(
      (packageItem: Package) => !packageItem.isPlusPackage
    );
    let includedCoverageList: any[] = [];
    let excludedCoverageList: any[] = [];
    if (
      selectedMainPackage &&
      !InsuranceHelper.isEmptyObject(selectedMainPackage?.includedCoverages)
    ) {
      includedCoverageList =
        selectedMainPackage?.includedCoverages.coverageList || [];
    }
    if (
      selectedMainPackage &&
      !InsuranceHelper.isEmptyObject(selectedMainPackage?.excludedCoverages)
    ) {
      excludedCoverageList =
        selectedMainPackage?.excludedCoverages.coverageList || [];
    }
    const fullCoverageList = [...includedCoverageList, ...excludedCoverageList];
    return (
      fullCoverageList.find(
        (coverage: Coverage) => coverage.name === coverageName
      )?.coverageItemLabel || ''
    );
  };

  static getTotalPriceInfoFromSelection = (
    selectedPackages: Package[],
    product: string
  ) => {
    const selectedPackageLabels: string[] = [];
    let totalPrice = 0;
    const additionalPackagesInfo: any = [];
    const currency: string = selectedPackages[0]?.currency || '';
    const paymentTerm: string = selectedPackages[0]?.priceFrequency || '';
    const paymentFrequency: string =
      selectedPackages[0]?.priceFrequency.split(' ').pop() || '';
    selectedPackages.forEach((item: Package) => {
      const packageName = item.packageHeaderLabel || item.packageName;
      if (
        !item.isPlusPackage ||
        (item.isPlusPackage &&
          InsuranceHelper.isAddOnsAvailableForProductType(product))
      ) {
        selectedPackageLabels.push(packageName);
        if (item.monthlyPrice) {
          totalPrice += Number(item.monthlyPrice);
        } else {
          totalPrice = 0;
        }
      }
      if (
        item.isPlusPackage &&
        InsuranceHelper.isAddOnsAvailableForProductType(product)
      ) {
        additionalPackagesInfo.push({
          packageName: item.packageHeaderLabel || item.packageName,
          packagePrice: item.monthlyPrice
        });
      }
    });
    return {
      selectedPackageLabels,
      totalPrice,
      currency,
      paymentTerm,
      paymentFrequency,
      additionalPackagesInfo
    };
  };

  static getSelectedInsurancePrice = (insurancePackageList: IPackageData[]) => {
    let selectedPrice = 0;
    if (insurancePackageList.length > 0) {
      selectedPrice =
        InsuranceHelper.getSelectedPackage(insurancePackageList)[0]
          ?.monthlyPrice;
    }
    return selectedPrice;
  };

  static getCoverageColumns = (packages: Package[], coverageName: string): NonEmptyArray<string> => {
    const coverageColumns: NonEmptyArray<string> = [""];
    packages.filter((packageObj) => !packageObj.isPlusPackage).forEach((packageObj) => {
      const isPresent = packageObj.includedCoverages.coverageList.find((row) => row.name === coverageName);
      const isContentPresent = packageObj.includedCoverages.coverageList.find((row) => row.name === coverageName)?.coverageItemLabel;
      coverageColumns.push(isContentPresent ? isContentPresent : isPresent ? "Y" : "N");
    });
    coverageColumns.shift();
    return coverageColumns.length > 0 ? coverageColumns : ["N"];
  }

  static getAdditionalPackagePrice = (
    additionalPackageList: IAdditionalPackageData[]
  ) => {
    return additionalPackageList[0].monthlyPrice
      ? additionalPackageList[0].monthlyPrice
      : 0;
  };

  static getFullListOfCoverages = (packages: Package[]) => {
    const fullCoverageList = packages.map((packageItem: Package) => {
      let includedCoverageList: any[] = [];
      let excludedCoverageList: any[] = [];
      if (!InsuranceHelper.isEmptyObject(packageItem?.includedCoverages)) {
        includedCoverageList =
          packageItem?.includedCoverages.coverageList || [];
      }
      if (!InsuranceHelper.isEmptyObject(packageItem?.excludedCoverages)) {
        excludedCoverageList =
          packageItem?.excludedCoverages.coverageList || [];
      }
      return {
        packageCode: packageItem.packageCode,
        coverageLists: [...includedCoverageList, ...excludedCoverageList]
      };
    });
    return fullCoverageList;
  };

  static isCoverageItemIncludedInPackage = (
    packageInfo: Package,
    coverage: string
  ) => {
    return packageInfo.includedCoverages?.coverageList.some(
      (includedCoverage: Coverage) => {
        return includedCoverage.name === coverage;
      }
    );
  };

  static buildErrorStatus = (
    apiResponse: any,
    statusCode: boolean | number | undefined
  ): ErrorStatus => {
    const errorDescription = apiResponse?.calculationStatus?.message
      ? apiResponse.calculationStatus.message
      : '';
    const errorTitle = apiResponse?.calculationStatus?.title
      ? apiResponse.calculationStatus.title
      : '';
    return InsuranceHelper.buildErrorMessage(
      errorTitle,
      errorDescription,
      statusCode
    );
  };

  static buildErrorMessage = (
    errorTitle: string,
    errorMessage: string,
    statusCode: boolean | number | undefined
  ) => {
    const channel = InsuranceHelper.getChannelInfo();
    const isHgf = channel?.toLowerCase() === 'hgf';
    const isRikshem = channel?.toLowerCase() === 'rikshem';
    const shouldAllowRetry = shouldAllowErrorRetry(statusCode);

    const statusCodeAndTitleExist = Boolean(statusCode && errorTitle);
    const statusCodeAndMessageExist = Boolean(statusCode && errorMessage);
    const statusCodeWithoutMessage = Boolean(statusCode && !errorMessage);

    let errorDescriptionGjensidigeCustomer = '';

    if (statusCodeAndMessageExist) {
      errorDescriptionGjensidigeCustomer = errorMessage;
    } else if (statusCodeWithoutMessage) {
      errorDescriptionGjensidigeCustomer = '';
    } else {
      errorDescriptionGjensidigeCustomer = isRikshem
        ?
        AppConstants.getAppText(
          'webshop.errorPage.defaultErrorPageDescriptionRikshem'
        )
        :
        AppConstants.getAppText(
          'webshop.errorPage.defaultErrorPageDescription'
        );
    }
    const errorDescription = isHgf
      ? AppConstants.getAppText(
        'webshop.errorPage.defaultErrorPageDescriptionHgf'
      )
      : errorDescriptionGjensidigeCustomer;


    const errorHeaderWithoutEdgeCase = statusCodeAndTitleExist
      ? errorTitle
      : AppConstants.getAppText('webshop.errorPage.defaultErrorPageHeader');
    const errorHeaderWithEdgeCase = errorHeader(
      statusCode,
      errorHeaderWithoutEdgeCase
    );
    return {
      errorPageHeader: errorHeaderWithEdgeCase,
      errorPageDescription: errorDescription,
      shouldAllowRetry: shouldAllowRetry,
      statusCode: statusCode,
      shouldDisplayError: true
    };
  };

  static getDefaultSelectedValue = (
    questions: PricingQuestion,
    apiKey: string,
    product: string
  ) => {
    let defaultValue =
      questions.options?.find((option) => option.isDefault)?.value || '';
    if (product === PRODUCTS.VILLA) {
      const customerInfo = JSON.parse(
        sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
      );
      if (customerInfo?.propertyData) {
        if (apiKey === 'area') {
          defaultValue = customerInfo?.propertyData?.area
            ? customerInfo?.propertyData?.area.toString()
            : defaultValue;
        }
        if (apiKey === 'builtYear') {
          defaultValue = customerInfo?.propertyData?.builtYear
            ? customerInfo?.propertyData?.builtYear.toString()
            : defaultValue;
        }
        if (apiKey === 'inspectionYear') {
          defaultValue = customerInfo?.propertyData?.inspectionYear
            ? customerInfo?.propertyData?.inspectionYear.toString()
            : defaultValue;
        }
      }
    }
    return defaultValue;
  };

  static getDefault = (
    apiKey: string,
    value: string,
    isDefault: boolean,
    product: string
  ) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    switch (product) {
      case PRODUCTS.CAR:
      case PRODUCTS.MC:
        if (apiKey === 'driverUnder24') {
          if (customerInfo.cprNumber) {
            isDefault = APIHelper.isDriverAgeBelow24(customerInfo.cprNumber)
              ? value === '2'
              : value === '1';
          }
        }
        break;
      case PRODUCTS.MOPED:
        if (apiKey === 'driverUnder18') {
          if (customerInfo.cprNumber) {
            isDefault = APIHelper.isCustomerUnderAge(customerInfo.cprNumber, 18)
              ? value === '2'
              : value === '1';
          }
        }
        break;
      case PRODUCTS.VILLA:
        if (customerInfo?.propertyData) {
          if (apiKey === 'roofConstruction') {
            isDefault = customerInfo?.propertyData?.roofConstruction
              ? value ===
              getRoofConstructionRange(
                Number(customerInfo?.propertyData?.roofConstruction)
              )
              : isDefault;
          }
        }
    }
    return isDefault;
  };
  static isDisabled = (apiKey: string, value: string, product: string) => {
    let disabled = false;
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    if (product === PRODUCTS.CAR || product === PRODUCTS.MC) {
      if (apiKey === 'driverUnder24') {
        if (customerInfo.cprNumber) {
          disabled =
            APIHelper.isDriverAgeBelow24(customerInfo.cprNumber) &&
            value === '1';
        }
      }
    }
    return disabled;
  };

  static isImmutable = (apiKey: string, product: string) => {
    let immutable = false;
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    if (product === PRODUCTS.VILLA && customerInfo?.propertyData) {
      if (apiKey === 'builtYear') {
        immutable = !!customerInfo?.propertyData?.builtYear;
      }
    }
    return immutable;
  };

  static getSelectedPackagesFromAPI = (offerResponse: OfferResponseAPI) => {
    return [
      ...InsuranceHelper.getSelectedPackage(offerResponse.packages),
      ...InsuranceHelper.getSelectedPackage(offerResponse?.additionalPackages)
    ];
  };

  static isAllPackagesNotAvailable = (packages: Package[]) =>
    packages.length === 0 ||
    packages.some(
      (packageItem: Package) =>
        !packageItem.monthlyPrice &&
        !packageItem.currency &&
        !packageItem.priceFrequency
    );

  static setPageTitle = (title: string) => (document.title = title);

  static isSummaryPageEnabled = (product: string) =>
    product === PRODUCTS.CAR || PRODUCTS.HOME || PRODUCTS.VILLA || PRODUCTS.MC || PRODUCTS.MOPED;

  static isFormWithInvalidAnswer = (questions: PricingQuestion[]) => {
    return questions.some((questionInfo: PricingQuestion) => {
      return (
        questionInfo.question &&
        questionInfo.controlType != 'Information' &&
        questionInfo.isVisible &&
        (!questionInfo.isValid || !questionInfo.selectedValue)
      );
    });
  };

  static isAdditionalInputsInvalidArea = (product: string, additionalInputs?: AdditionalInputs[]) => {
    if (product === PRODUCTS.VILLA && additionalInputs && additionalInputs.length > 0) {
      const totalArea = additionalInputs.reduce((acc, item) => acc + Number(item.area?.value), 0);
      return totalArea > MAX_AREA_ADDITIONAL_BUILDINGS;
    }
    return false;
  }

  static shouldDispayAddonPlusSign = (product: string) =>
    product === PRODUCTS.CAR;

  static shouldUpdateSelectionOnAllPackage = (product: string) =>
    product === PRODUCTS.HOME;

  static shouldDispayPlusPackage = (product: string, packageTile: Package) => {
    switch (product) {
      case PRODUCTS.CAR:
      case PRODUCTS.HOME:
        return packageTile.isEnabled;
    }
  };

  static isCustomerEligibilityCheckEnabled = (
    product: string,
    page: string
  ) => {
    const policyNumber = InsuranceHelper.retrievePolicyInfoFromURL()?.policyNumber || InsuranceHelper.getPolicyAndChannelFromUrl()?.policyNumber;
    const isPolicyAlreadyCreated = policyNumber.length > 0;
    return page === ROUTES.moreInputsPage && !isPolicyAlreadyCreated;
  };

  static shouldSelectExcludedPackage = (
    product: string,
    packageTile: Package
  ) => {
    switch (product) {
      case PRODUCTS.CAR:
        return false;
      case PRODUCTS.HOME:
        return packageTile.isSelected;
      default:
        return false;
    }
  };

  static shouldEnablePlusPackageByDefault = (
    product: string,
    packageTile: Package
  ) => {
    switch (product) {
      case PRODUCTS.CAR:
        return packageTile.isEnabled;
      case PRODUCTS.HOME:
        return InsuranceHelper.isAddOnsAvailableForProductType(product);
      default:
        return packageTile.isEnabled;
    }
  };

  static isAddOnsAvailableForProductType = (product: string) => {
    switch (product) {
      case PRODUCTS.CAR:
        return true;
      case PRODUCTS.HOME: {
        const customerInfo = JSON.parse(
          sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
        );
        const channel = customerInfo?.channel || '';
        return (
          customerInfo &&
          !InsuranceHelper.isAddOnDisabledForChannel(channel) &&
          customerInfo?.houseType &&
          customerInfo?.houseType.toString() === '2'
        );
      }
      default:
        return true;
    }
  };

  static getDisabledAddonMessage = (product: string) => {
    if (product === PRODUCTS.CAR) {
      return AppConstants.getAppText('webshop.plusPacket.disableMessageCar');
    } else {
      return AppConstants.getAppText('webshop.plusPacket.disableMessage');
    }
  }

  static isSingleCoverageProduct = (product: string) => product === PRODUCTS.ACCIDENT || product === PRODUCTS.PPI;

  static isAddOnDisabledForChannel = (channel: string) =>
    ADD_ON_PACKAGE_DISABLED_PARTNERS.includes(channel.toLowerCase());

  static getPolicyCreationStatus = (product: string, packages: Package[]) => {
    const policyData = InsuranceHelper.retrievePolicyInfoFromURL();
    return {
      isPolicyCreated:
        (!!policyData.policyNumber && !!policyData.customerNumber)
        || (!!policyData.policyNumber && !policyData.hasSessionData),
      isAllPackagesNotAvailable:
        InsuranceHelper.isAllPackagesNotAvailable(packages),
      policyNumber: policyData.policyNumber
    }
  };

  static getTotalCoverageInfo = (
    product: string,
    selectedPackageCode: string
  ) => {
    let totalCoverageForSelectedPackage: any = [];
    const totalCoverageForProduct = totalCoverageConfig.find(
      (totalCoverage: any) =>
        totalCoverage.product.toLowerCase() === product.toLowerCase()
    );
    const totalCoverageInfo =
      totalCoverageForProduct && totalCoverageForProduct.totalCoverageInfo
        ? totalCoverageForProduct.totalCoverageInfo
        : [];
    if (totalCoverageInfo.length > 0) {
      totalCoverageForSelectedPackage =
        totalCoverageInfo.filter(
          (coverageInfo: TotalCoverageInfo) =>
            coverageInfo.packageCode === selectedPackageCode
        ) || [];
    }
    return totalCoverageForSelectedPackage;
  };

  static prepareSummaryData = (
    product: string,
    questionsInfo: PricingStepConfig[],
    offerInfo: Offer,
    priceSummary: TotalPriceInfo,
    selectedPackages: Package[],
    additionalQuestionsInfo?: AdditionalInputs[]
  ) => {
    const summaryBrief = [];
    const briefSelections: {
      label: string;
      value: string;
      noLineBreakOnValue?: boolean;
    }[] = [];
    const summaryDetails = [];
    const detailsSelections: {
      label: string;
      value: string;
      noLineBreakOnValue?: boolean;
    }[] = [];
    let deductibleAmount = 0;
    const templateQuestions = questionsInfo.flatMap(
      (questionsInfo) => questionsInfo.questions
    );
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );

    switch (product) {
      case PRODUCTS.CAR: {
        offerInfo.customerName &&
          briefSelections.push({
            label: AppConstants.getAppText('webshop.summary.customerNameLabel'),
            value: offerInfo.customerName
          });
        briefSelections.push({
          label: AppConstants.getAppText(
            'webshop.summary.car.trafikDeductibleLabel'
          ),
          value: '1 000 kr'
        });
        customerInfo?.registrationNumber &&
          detailsSelections.push({
            label: 'Registreringsnummer',
            value: customerInfo?.registrationNumber
          });
        offerInfo.objectDescription &&
          detailsSelections.push({
            label: AppConstants.getAppText(
              'webshop.summary.insuredObjectLabel'
            ),
            value: offerInfo.objectDescription
          });

        templateQuestions.forEach((question: PricingQuestion) => {
          if (
            question.isVisible &&
            (question.APIKey === 'vagnskadaSelfRisk' ||
              question.APIKey === 'delkaskoSelfRisk')
          ) {
            const selectedOption =
              question.options &&
              question.options.find(
                (option) => option.value === question.selectedValue
              );
            deductibleAmount = parseInt(
              selectedOption?.label.replace(/\D/g, '') || '0'
            );
            deductibleAmount &&
              briefSelections.push({
                label:
                  question.APIKey === 'delkaskoSelfRisk'
                    ? AppConstants.getAppText(
                      'webshop.summary.car.delkaskoLabel'
                    )
                    : AppConstants.getAppText(
                      'webshop.summary.car.vagnskadaLabel'
                    ),
                value: `${deductibleAmount.toLocaleString('sv-SE')} ${priceSummary.currency
                  }`
              });
          }
          if (question.APIKey === 'driverUnder24') {
            const selectedOption = question.options?.find(
              (option) => option.value === question.selectedValue
            );
            const selectedValue = selectedOption?.label || '';
            selectedValue &&
              detailsSelections.push({
                label: AppConstants.getAppText(
                  'webshop.summary.driverAge.label'
                ),
                value: selectedValue
              });
          }
          if (question.APIKey === 'yearlyKm') {
            const selectedOption = question.options?.find(
              (option) => option.value === question.selectedValue
            );
            const selectedValue = selectedOption?.label || '';
            selectedValue &&
              detailsSelections.push({
                label: AppConstants.getAppText(
                  'webshop.summary.car.kmDrivenLabel'
                ),
                value: selectedValue
              });
          }
        });
        summaryBrief.push({
          sectionHeader: AppConstants.getAppText(
            'webshop.summary.overviewHeader'
          ),
          selections: briefSelections
        });
        summaryDetails.push({
          sectionHeader: AppConstants.getAppText(
            'webshop.summary.objectDescriptionHeader'
          ),
          selections: detailsSelections
        });

        break;
      }
      case PRODUCTS.MOPED: {
        offerInfo.customerName &&
          briefSelections.push({
            label: AppConstants.getAppText('webshop.summary.customerNameLabel'),
            value: offerInfo.customerName
          });
        customerInfo?.registrationNumber &&
          detailsSelections.push({
            label: 'Registreringsnummer',
            value: customerInfo?.registrationNumber
          });
        offerInfo.objectDescription &&
          detailsSelections.push({
            label: AppConstants.getAppText(
              'webshop.summary.insuredObjectLabel'
            ),
            value: offerInfo.objectDescription
          });

        templateQuestions.forEach((question: PricingQuestion) => {
          if (question.APIKey === 'driverUnder18') {
            const selectedOption = question.options?.find(
              (option) => option.value === question.selectedValue
            );
            const selectedValue = selectedOption?.label || '';
            selectedValue &&
              detailsSelections.push({
                label: 'Förare under 18 år',
                value: selectedValue
              });
          }
        });

        summaryBrief.push({
          sectionHeader: AppConstants.getAppText(
            'webshop.summary.overviewHeader'
          ),
          selections: briefSelections
        });
        summaryDetails.push({
          sectionHeader: AppConstants.getAppText(
            'webshop.summary.objectDescriptionHeader'
          ),
          selections: detailsSelections
        });

        break;
      }
      case PRODUCTS.MC: {
        offerInfo.customerName &&
          briefSelections.push({
            label: AppConstants.getAppText('webshop.summary.customerNameLabel'),
            value: offerInfo.customerName
          });
        briefSelections.push({
          label: AppConstants.getAppText(
            'webshop.summary.mc.trafikDeductibleLabel'
          ),
          value: '1 000 kr'
        });
        const packageCode = selectedPackages[0]?.packageCode || '';
        if (packageCode === 'MOTORCYCLE_PACK_001' || packageCode === 'MOTORCYCLE_PACK_002') {
          briefSelections.push({
            label: AppConstants.getAppText(
              'webshop.summary.mc.theftDeductibleLabel'
            ),
            value: '3 000 kr'
          });
        }
        if (packageCode === 'MOTORCYCLE_PACK_001') {
          briefSelections.push({
            label: AppConstants.getAppText(
              'webshop.summary.mc.vagnskadaDeductibleLabel'
            ),
            value: '5 000 kr'
          });
        }
        customerInfo?.registrationNumber &&
          detailsSelections.push({
            label: 'Registreringsnummer',
            value: customerInfo?.registrationNumber
          });
        offerInfo.objectDescription &&
          detailsSelections.push({
            label: AppConstants.getAppText(
              'webshop.summary.insuredObjectLabel'
            ),
            value: offerInfo.objectDescription
          });

        templateQuestions.forEach((question: PricingQuestion) => {
          if (question.APIKey === 'mcType') {
            const selectedOption = question.options?.find(
              (option) => option.value === question.selectedValue
            );
            const selectedValue = selectedOption?.label || '';
            selectedValue &&
              detailsSelections.push({
                label: AppConstants.getAppText(
                  'webshop.summary.mc.nightParkingLabel'
                ),
                value: selectedValue
              });
          }
          if (question.APIKey === 'driverUnder24') {
            const selectedOption = question.options?.find(
              (option) => option.value === question.selectedValue
            );
            const selectedValue = selectedOption?.label || '';
            selectedValue &&
              detailsSelections.push({
                label: AppConstants.getAppText(
                  'webshop.summary.driverAge.label'
                ),
                value: selectedValue
              });
          }
        });
        summaryBrief.push({
          sectionHeader: AppConstants.getAppText(
            'webshop.summary.overviewHeader'
          ),
          selections: briefSelections
        });
        summaryDetails.push({
          sectionHeader: AppConstants.getAppText(
            'webshop.summary.objectDescriptionHeader'
          ),
          selections: detailsSelections
        });

        break;
      }
      case PRODUCTS.HOME: {
        offerInfo.customerName &&
          briefSelections.push({
            label: AppConstants.getAppText('webshop.summary.customerNameLabel'),
            value: offerInfo.customerName
          });
        briefSelections.push({
          label: AppConstants.getAppText('webshop.deductible.label'),
          value: '1 800 kr'
        });
        const theftFireLeakCoverage =
          InsuranceHelper.getCoverageValueFromSelectedPackage(
            selectedPackages,
            'Stöld, brand & vattenläcka'
          );
        theftFireLeakCoverage &&
          briefSelections.push({
            label: AppConstants.getAppText(
              'webshop.summary.home.theftFireLeakLabel'
            ),
            value: theftFireLeakCoverage,
            noLineBreakOnValue: true
          });
        const travelProtectionCoverage =
          InsuranceHelper.getCoverageValueFromSelectedPackage(
            selectedPackages,
            'Reseskydd'
          );
        travelProtectionCoverage &&
          briefSelections.push({
            label: AppConstants.getAppText(
              'webshop.summary.home.travelProtectionLabel'
            ),
            value: travelProtectionCoverage,
            noLineBreakOnValue: true
          });
        customerInfo.customerAddress &&
          detailsSelections.push({
            label: AppConstants.getAppText(
              'webshop.summary.insuredObjectLabel'
            ),
            value: customerInfo.customerAddress
          });

        customerInfo.zipCode &&
          detailsSelections.push({
            label: AppConstants.getAppText(
              'webshop.summary.insuredObjectZipCode'
            ),
            value: TextHelper.formatZipCode(customerInfo.zipCode)
          });
        templateQuestions.forEach((question: PricingQuestion) => {
          if (question.APIKey === 'coverageAmount') {
            const selectedOption = question.options?.find(
              (option) => option.value === question.selectedValue
            );
            const selectedValue = selectedOption?.label || '';
            selectedValue &&
              briefSelections.splice(2, 0, {
                label: 'Försäkringsbelopp',
                value: selectedValue
              });
          }
          if (question.APIKey === 'area') {
            question.selectedValue &&
              detailsSelections.push({
                label: AppConstants.getAppText('webshop.home.livingSpace'),
                value: `${question.selectedValue} m²`
              });
          }
          if (question.APIKey === 'noOfPeople') {
            const selectedOption = question.options?.find(
              (option) => option.value === question.selectedValue
            );
            const selectedValue = selectedOption?.label || '';
            selectedValue &&
              detailsSelections.push({
                label: AppConstants.getAppText(
                  'webshop.summary.home.noOfPeopleLabel'
                ),
                value: selectedValue
              });
          }
        });

        summaryBrief.push({
          sectionHeader: AppConstants.getAppText(
            'webshop.summary.overviewHeader'
          ),
          selections: briefSelections
        });
        summaryDetails.push({
          sectionHeader: AppConstants.getAppText(
            'webshop.summary.objectDescriptionHeader'
          ),
          selections: detailsSelections
        });
        break;
      }
      case PRODUCTS.VILLA: {
        offerInfo.customerName &&
          briefSelections.push({
            label: AppConstants.getAppText('webshop.summary.customerNameLabel'),
            value: offerInfo.customerName
          });
        const insuredAmount =
          InsuranceHelper.getCoverageValueFromSelectedPackage(
            selectedPackages,
            'Skydd för dina saker'
          );
        insuredAmount &&
          briefSelections.push({
            label: AppConstants.getAppText(
              'webshop.summary.villa.insuredAmountLabel'
            ),
            value: insuredAmount,
            noLineBreakOnValue: true
          });
        const deductibleInfo = ConfigHelper.getDeductibleInfo(
          PRODUCTS.VILLA,
          selectedPackages[0].packageCode
        );
        if (deductibleInfo && !InsuranceHelper.isEmptyObject(deductibleInfo)) {
          deductibleInfo?.deductibleBuilding &&
            briefSelections.push({
              label: AppConstants.getAppText(
                'webshop.summary.villa.deductibleBuilding'
              ),
              value: deductibleInfo.deductibleBuilding,
              noLineBreakOnValue: true
            });
          deductibleInfo?.deductibleSelfRisk &&
            briefSelections.push({
              label: AppConstants.getAppText(
                'webshop.summary.villa.deductibleMovable'
              ),
              value: deductibleInfo.deductibleSelfRisk,
              noLineBreakOnValue: true
            });
        }
        customerInfo.customerAddress &&
          detailsSelections.push({
            label: AppConstants.getAppText(
              'webshop.summary.insuredObjectLabel'
            ),
            value: customerInfo.customerAddress
          });

        customerInfo.zipCode &&
          detailsSelections.push({
            label: AppConstants.getAppText(
              'webshop.summary.insuredObjectZipCode'
            ),
            value: TextHelper.formatZipCode(customerInfo.zipCode)
          });

        // customerInfo.area && detailsSelections.push({
        //   label: AppConstants.getAppText('webshop.home.livingSpace'),
        //   value: customerInfo.area
        // })
        templateQuestions.forEach((question: PricingQuestion) => {
          if (question.APIKey === 'roofConstruction') {
            const selectedOption = question.options?.find(
              (option) => option.value === question.selectedValue
            );
            const selectedValue = selectedOption?.label || '';
            selectedValue &&
              detailsSelections.push({
                label: 'Biyta',
                value: selectedValue
              });
          }
          if (question.APIKey === 'area') {
            question.selectedValue &&
              detailsSelections.push({
                label: AppConstants.getAppText('webshop.home.livingSpace'),
                value: `${question.selectedValue} m²`
              });
          }
          if (question.APIKey === 'inspectionYear') {
            question.selectedValue &&
              detailsSelections.push({
                label: AppConstants.getAppText(
                  'webshop.summary.villa.boughtYear'
                ),
                value: question.selectedValue
              });
          }
          if (question.APIKey === 'builtYear') {
            question.selectedValue &&
              detailsSelections.push({
                label: AppConstants.getAppText(
                  'webshop.summary.villa.builtYear'
                ),
                value: question.selectedValue
              });
          }
          if (question.APIKey === 'basement') {
            const selectedOption = question.options?.find(
              (option) => option.value === question.selectedValue
            );
            const selectedValue = selectedOption?.label || '';
            selectedValue &&
              detailsSelections.push({
                label: AppConstants.getAppText(
                  'webshop.summary.villa.basement'
                ),
                value: selectedValue
              });
          }
          if (question.APIKey === 'noOfPeople') {
            const selectedOption = question.options?.find(
              (option) => option.value === question.selectedValue
            );
            const selectedValue = selectedOption?.label || '';
            selectedValue &&
              detailsSelections.push({
                label: AppConstants.getAppText(
                  'webshop.summary.home.noOfPeopleLabel'
                ),
                value: selectedValue
              });
          }
          if (question.APIKey === 'energyType') {
            const selectedOption = question.options?.find(
              (option) => option.value === question.selectedValue
            );
            const selectedValue = selectedOption?.label || '';
            selectedValue &&
              detailsSelections.push({
                label: AppConstants.getAppText(
                  'webshop.summary.villa.waterHeating'
                ),
                value: selectedValue
              });
          }
          if (question.APIKey === 'noOfFloors') {
            const selectedOption = question.options?.find(
              (option) => option.value === question.selectedValue
            );
            const selectedValue = selectedOption?.label || '';
            selectedValue &&
              detailsSelections.push({
                label: AppConstants.getAppText(
                  'webshop.summary.villa.numberOfWetRooms'
                ),
                value: selectedValue
              });
          }
        });
        const additionalBuildings: any = [];
        additionalQuestionsInfo?.map((buildingInfo: any) => {
          buildingInfo.type.label &&
            additionalBuildings.push({
              label: buildingInfo.type.label,
              value: `${buildingInfo.area.label}, ${buildingInfo.energyType.value === '3'
                ? 'inget indraget vatten'
                : 'indraget vatten'
                }`,
              noLineBreakOnValue: true
            });
        });
        summaryBrief.push({
          sectionHeader: AppConstants.getAppText(
            'webshop.summary.overviewHeader'
          ),
          selections: briefSelections
        });
        summaryDetails.push({
          sectionHeader: AppConstants.getAppText(
            'webshop.summary.objectDescriptionHeader'
          ),
          selections: detailsSelections
        });
        if (additionalBuildings.length > 0) {
          summaryDetails.push({
            sectionHeader: AppConstants.getAppText(
              'webshop.summary.villa.otherBuildingHeader'
            ),
            selections: additionalBuildings
          });
        }

        break;
      }
    }
    return {
      summaryBrief,
      summaryDetails
    };
  };

  static getSummaryDetails = (
    product: string,
    questionsInfo: PricingStepConfig[],
    offerInfo: Offer,
    priceSummary: TotalPriceInfo,
    selectedPackages: Package[],
    additionalQuestionsInfo?: AdditionalInputs[]
  ) => {
    const summaryData = InsuranceHelper.prepareSummaryData(
      product,
      questionsInfo,
      offerInfo,
      priceSummary,
      selectedPackages,
      additionalQuestionsInfo
    );
    const summaryDetails = {
      selectedPackageName: priceSummary.selectedPackageLabels.join(' + '),
      selectedPackagePrice: `${priceSummary.totalPrice.toLocaleString(
        'sv-SE'
      )} ${priceSummary.currency}`,
      summaryBrief: summaryData.summaryBrief,
      summaryDetails: summaryData.summaryDetails
    };
    return summaryDetails;
  };

  static getDependentQuestions = (
    product: string,
    questionType: string | boolean,
    stepQuestions?: PricingStepConfig
  ) => {
    const dependentQuestion = {
      type: '',
      value: ''
    };
    if (product === PRODUCTS.VILLA && questionType === 'builtYear') {
      dependentQuestion.value =
        stepQuestions?.questions.find(
          (question) => question.APIKey === 'inspectionYear'
        )?.selectedValue || '';
      dependentQuestion.type = 'inspectionYear';
    }
    return dependentQuestion;
  };

  static isPackageExpandedByDefault = (product: string) =>
    product === PRODUCTS.CAR;
  static isAdditionalInputsEnabled = (product: string) =>
    product === PRODUCTS.VILLA;
  static getSelectedLabel = (question: IPricingQuestion) => {
    const selectedOption = question.options?.find(
      (option: IValueOptions) => option.value === question.selectedValue
    );
    return selectedOption?.label || '';
  };
  static prepareAdditionalQuestions = (
    additionalModalInputs: IPricingQuestion[]
  ) => {
    let additionalQuestions = {
      type: {
        label: '',
        value: ''
      },
      area: {
        label: '',
        value: ''
      },
      energyType: {
        label: '',
        value: ''
      }
    };
    if (additionalModalInputs.length > 0) {
      let selectedType = {
        label: '',
        value: ''
      };
      let selectedArea = {
        label: '',
        value: ''
      };
      let selectedEnergyType = {
        label: '',
        value: ''
      };
      additionalModalInputs.map((question: IPricingQuestion) => {
        if (question.APIKey === 'extraFloors') {
          selectedType = {
            label: InsuranceHelper.getSelectedLabel(question),
            value: question.selectedValue
          };
        }
        if (question.APIKey === 'extraBuildingArea') {
          selectedArea = {
            label: InsuranceHelper.getSelectedLabel(question),
            value: question.selectedValue
          };
        }
        if (question.APIKey === 'extraFront') {
          selectedEnergyType = {
            label: InsuranceHelper.getSelectedLabel(question),
            value: question.selectedValue
          };
        }
      });
      if (
        selectedType.label !== '' &&
        selectedType.label !== '' &&
        selectedType.label !== ''
      ) {
        additionalQuestions = {
          type: selectedType,
          area: selectedArea,
          energyType: selectedEnergyType
        };
      }
    }
    return additionalQuestions;
  };
}

